import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorPage } from '@/components/common';
import { FaExclamationTriangle } from 'react-icons/fa';
import { getDynatraceLogger } from '@/Services';
import { notifyCloseModal } from '@/Utils/iframe-utils';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 * 
 * Based on React Error Boundary best practices
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const logger = getDynatraceLogger();
    
    logger?.logError('Error Boundary caught error', error, {
      componentStack: errorInfo.componentStack,
      errorMessage: error.message,
      errorName: error.name,
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    notifyCloseModal();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorPage
          icon={FaExclamationTriangle}
          title="Something went wrong"
          message="An unexpected error occurred. Please try again later."
          buttonText="Exit"
          onButtonClick={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}
